// Tour module hooks
import { useCallback, useState } from 'react';
import { tourService } from '../services/tourService';
import { Tour, TourForm } from '../types';
import { useApi, useMutation } from '../../../shared/hooks/useApi';

export interface UseToursProps {
  museumCode?: string;
  isActive?: boolean;
}

export function useTours({ museumCode, isActive }: UseToursProps = {}) {
  return useApi(
    () => tourService.getTours({ museum_code: museumCode, is_active: isActive }),
    [museumCode, isActive],
    true, // Always fetch - museumCode can be undefined for "All Museums"
  );
}

export function useTour(id: number) {
  return useApi(() => tourService.getTour(id), [id], !!id);
}

export function useMuseums() {
  return useApi(() => tourService.getMuseums(), []);
}

export function useWaypointsByMuseum() {
  const [waypoints, setWaypoints] = useState<any[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const fetchWaypointsByMuseum = useCallback(async (museumCode: string) => {
    setLoading(true);
    setError(null);
    try {
      const result = await tourService.getWaypointsByMuseum(museumCode);
      setWaypoints(result);
    } catch (err: any) {
      setError(err.message || 'Failed to fetch waypoints');
    } finally {
      setLoading(false);
    }
  }, []);

  return {
    waypoints,
    loading,
    error,
    fetchWaypointsByMuseum,
  };
}

export function useCreateTour() {
  return useMutation((data: TourForm) => tourService.createTour(data), {
    onSuccess: () => {
      console.log('Tour created successfully');
    },
    onError: error => {
      console.error('Failed to create tour:', error);
    },
  });
}

export function useUpdateTour() {
  return useMutation(({ id, data }: { id: number; data: Partial<TourForm> }) => tourService.updateTour(id, data), {
    onSuccess: () => {
      console.log('Tour updated successfully');
    },
    onError: error => {
      console.error('Failed to update tour:', error);
    },
  });
}

export function useDeleteTour() {
  return useMutation((id: number) => tourService.deleteTour(id), {
    onSuccess: () => {
      console.log('Tour deleted successfully');
    },
    onError: error => {
      console.error('Failed to delete tour:', error);
    },
  });
}

// Utility hooks
export function useToursByMuseum() {
  const [tours, setTours] = useState<Tour[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const fetchToursByMuseum = async (museumCode: string) => {
    setLoading(true);
    setError(null);
    try {
      const result = await tourService.getToursByMuseum(museumCode);
      setTours(result);
    } catch (err: any) {
      setError(err.message || 'Failed to fetch tours');
    } finally {
      setLoading(false);
    }
  };

  return {
    tours,
    loading,
    error,
    fetchToursByMuseum,
  };
}

export function useActiveTours() {
  const [tours, setTours] = useState<Tour[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const fetchActiveTours = async (museumCode?: string) => {
    setLoading(true);
    setError(null);
    try {
      const result = await tourService.getActiveTours(museumCode);
      setTours(result);
    } catch (err: any) {
      setError(err.message || 'Failed to fetch active tours');
    } finally {
      setLoading(false);
    }
  };

  return {
    tours,
    loading,
    error,
    fetchActiveTours,
  };
}
